import { CalendarStatus } from '@prisma/client'
import { useContext, useMemo } from 'react'
import { LocaleContext } from 'components/common/LocaleContext'
import { getDateForTimezone } from 'components/common/date.utils'
import Race from 'containers/event/Race'
import { LightGroup } from 'domain/models/Group/EntityGroup'
import { Sponsor } from 'domain/models/Sponsor/Sponsor'
import { NonNullLiveEdition } from 'services/api/database/EventService'
import { raceSort } from './utils'

interface RacesProps {
  filteredRaces: NonNullLiveEdition['races']
  buildCtaRegisterLink: (
    slug: string | undefined | null
  ) => string | undefined | null
  websiteUrl?: string | null
  facebookUrl?: string | null
  isFeatured: boolean | null
  sponsors?: Sponsor[]
  registrationOpeningDate: Date | null
  registrationClosingDate: Date | null
  calendarStatus: CalendarStatus
  group: LightGroup | null
}
export const DATE_TO_BE_CONFIRMED = 'dateToBeConfirmed'

const Races = ({
  filteredRaces,
  buildCtaRegisterLink,
  websiteUrl,
  facebookUrl,
  isFeatured,
  sponsors,
  registrationOpeningDate,
  registrationClosingDate,
  calendarStatus,
  group
}: RacesProps) => {
  const { dateStringLocale } = useContext(LocaleContext)

  const racesByDate = useMemo(() => {
    let uniqueId: string | undefined
    return [...filteredRaces].sort(raceSort).reduce((acc, race, index) => {
      if (race.startDate) {
        const displayDate = getDateForTimezone(
          race.startDate,
          race.timeZone,
          dateStringLocale,
          calendarStatus === CalendarStatus.TO_BE_CONFIRMED
            ? {
                month: 'long',
                year: 'numeric'
              }
            : {
                weekday: 'short',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              }
        )
        uniqueId = uniqueId ?? `${displayDate}-${index}`

        if (!acc[uniqueId]) {
          acc[uniqueId] = { displayDate, races: [] }
        } else if (acc[uniqueId]!.displayDate !== displayDate) {
          uniqueId = `${displayDate}-${index}`
          acc[uniqueId] = { displayDate, races: [] }
        }
        acc[uniqueId]!.races.push(race)
      } else {
        if (!acc[DATE_TO_BE_CONFIRMED]) {
          acc[DATE_TO_BE_CONFIRMED] = { races: [] }
        }
        acc[DATE_TO_BE_CONFIRMED].races.push(race)
      }
      return acc
    }, {} as Record<string, { displayDate?: string; races: typeof filteredRaces }>)
  }, [filteredRaces])

  return Object.entries(racesByDate).map(
    ([uniqueId, { races, displayDate }]) => (
      <Race
        key={uniqueId}
        date={displayDate ?? uniqueId}
        racesForDate={races}
        buildCtaRegisterLink={buildCtaRegisterLink}
        websiteUrl={websiteUrl}
        facebookUrl={facebookUrl}
        isFeatured={isFeatured}
        sponsors={sponsors}
        editionRegistrationClosingDate={registrationClosingDate}
        editionRegistrationOpeningDate={registrationOpeningDate}
        calendarStatus={calendarStatus}
        group={group}
      />
    )
  )
}

export default Races
